import { ReactNode } from "react";
import { useIntl } from "react-intl";
import { styled } from "@mui/material/styles";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";

type ToggleOption<T extends string | number> = {
  value: T;
  // i18n message id, resolved in the user's locale.
  label: string;
  // Verbatim label, shown as-is when set (e.g. "Spill", never translated).
  // Takes precedence over `label`.
  labelText?: string;
  // Optional node rendered next to the label (e.g. an alert dot).
  adornment?: ReactNode;
};

type ExclusiveToggleGroupProps<T extends string | number> = {
  value: T;
  options: ToggleOption<T>[];
  onChange: (value: T) => void;
  // MUI-like size: "small" (24px, default), "medium" (28px, lines up with a
  // compact text input) or "large" (40px, the height of a dense MUI input, so
  // a toggle and a field sit on the same baseline in a form).
  size?: "small" | "medium" | "large";
};

const GROUP_HEIGHTS = { small: 24, medium: 28, large: 40 } as const;

const StyledGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  "& .MuiToggleButton-root": {
    backgroundColor: theme.vars.palette.filter.toggleBackground,
    borderColor: theme.vars.palette.filter.toggleBorder,
    textTransform: "initial",
    color: theme.vars.palette.text.primary,
    fontSize: "0.8125rem",
    padding: theme.spacing(0.5, 1),
    gap: theme.spacing(0.5),
    "&.Mui-selected": {
      color: theme.vars.palette.filter.toggleSelectedText,
      backgroundColor: theme.vars.palette.filter.toggleSelectedBackground,
      "&:hover": {
        backgroundColor:
          theme.vars.palette.filter.toggleSelectedBackgroundHover,
      },
    },
    "&:hover": {
      backgroundColor: theme.vars.palette.filter.toggleBackgroundHover,
    },
  },
}));

const ExclusiveToggleGroup = <T extends string | number>({
  value,
  options,
  onChange,
  size = "small",
}: ExclusiveToggleGroupProps<T>) => {
  const intl = useIntl();

  return (
    <StyledGroup
      value={value}
      exclusive
      onChange={(_, newValue) => {
        if (newValue !== null) onChange(newValue);
      }}
      size="small"
      sx={{ height: GROUP_HEIGHTS[size] }}
    >
      {options.map((opt) => (
        <ToggleButton key={opt.value} value={opt.value}>
          {opt.labelText ?? intl.formatMessage({ id: opt.label })}
          {opt.adornment}
        </ToggleButton>
      ))}
    </StyledGroup>
  );
};

export default ExclusiveToggleGroup;
